import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../features/auth/AuthProvider";
import {
  profileApi,
  type Profile,
  type SecurityProfile,
} from "../features/profile/profileApi";
import { pushNotifications } from "../features/notification/pushNotifications";
import { resolveApiUrl } from "../shared/api/request";
import { useModal } from "../shared/ui/useModal";
import { LoadingIndicator } from "../shared/ui/LoadingIndicator";
import {
  applyFontSizePreference,
  getFontSizePreference,
  type FontSizePreference,
} from "../features/display/fontSizePreference"
import { publicAsset } from "../shared/publicPath";

export function ProfilePage({
  api = profileApi,
  push = pushNotifications,
}: {
  api?: typeof profileApi;
  push?: typeof pushNotifications;
}) {
  const { logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const modal = useModal();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [security, setSecurity] = useState<SecurityProfile | null>(null);
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatar, setAvatar] = useState<File | null>(null);
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [securityError, setSecurityError] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [view, setView] = useState<
    "home" | "edit" | "security" | "notifications" | "display"
  >("home");
  const [pushConfig, setPushConfig] = useState<{
    enabled: boolean;
    publicKey: string;
  } | null>(null);
  const [pushSubscription, setPushSubscription] =
    useState<PushSubscription | null>(null);
  const [info, setInfo] = useState<{ title: string; content: string } | null>(
    null,
  );
  const [fontSize, setFontSize] = useState<FontSizePreference>(
    getFontSizePreference,
  );
  const [securityPanel, setSecurityPanel] = useState<
    "email" | "password" | null
  >(null);

  function selectFontSize(value: FontSizePreference) {
    setFontSize(value);
    applyFontSizePreference(value);
  }

  useEffect(() => {
    let active = true;
    void api
      .getProfile()
      .then((next) => {
        if (active) applyProfile(next);
      })
      .catch((reason) => {
        if (active) setError(message(reason, "资料加载失败"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    void api
      .getSecurityProfile()
      .then((next) => {
        if (active) applySecurity(next);
      })
      .catch((reason) => {
        if (active) setSecurityError(message(reason, "账号安全信息加载失败"));
      })
      .finally(() => {
        if (active) setSecurityLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  function applyProfile(next: Profile) {
    setProfile(next);
    setName(next.name || "");
    setAvatarUrl(next.avatarUrl || "");
  }
  function applySecurity(next: SecurityProfile) {
    setSecurity(next);
    setEmail(next.email || "");
    setEmailCode("");
  }
  function begin(action: string) {
    setBusy(action);
    setError("");
    setSuccess("");
  }
  function finish() {
    setBusy("");
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    const nickname = name.trim();
    if (!nickname) {
      setError("请输入昵称");
      return;
    }
    begin("profile");
    try {
      let nextAvatar = avatarUrl;
      if (avatar) nextAvatar = (await api.uploadAvatar(avatar)).avatarUrl;
      const next = await api.updateProfile({ nickname, avatarUrl: nextAvatar });
      applyProfile(next);
      setAvatar(null);
      updateUser({ name: next.name, avatarUrl: next.avatarUrl || "" });
      setSuccess("个人资料已保存");
    } catch (reason) {
      setError(message(reason, "资料保存失败"));
    } finally {
      finish();
    }
  }

  async function sendEmailCode() {
    const value = email.trim();
    if (!value) {
      setError("请输入邮箱");
      return;
    }
    begin("email-code");
    try {
      await api.sendEmailCode(value);
      setSuccess("验证码已发送");
    } catch (reason) {
      setError(message(reason, "验证码发送失败"));
    } finally {
      finish();
    }
  }
  async function bindEmail(event: FormEvent) {
    event.preventDefault();
    const value = email.trim();
    const code = emailCode.trim();
    if (!value || !code) {
      setError("请输入邮箱和验证码");
      return;
    }
    begin("email");
    try {
      applySecurity(await api.bindEmail(value, code));
      setSecurityPanel(null);
      setSuccess("邮箱已绑定");
    } catch (reason) {
      setError(message(reason, "邮箱绑定失败"));
    } finally {
      finish();
    }
  }
  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (!currentPassword || newPassword.length < 8) {
      setError("请输入当前密码，新密码至少 8 位");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("两次输入的新密码不一致");
      return;
    }
    begin("password");
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSecurityPanel(null);
      setSuccess("密码已更新");
    } catch (reason) {
      setError(message(reason, "密码修改失败"));
    } finally {
      finish();
    }
  }
  async function handleLogout() {
    if (
      !(await modal.confirm("退出后需要重新登录才能继续使用。", {
        title: "退出登录",
        confirmLabel: "退出",
        danger: true,
      }))
    )
      return;
    begin("logout");
    try {
      const subscription = push.supported() ? await push.current() : null;
      if (subscription) {
        await api.deletePushSubscription(subscription.endpoint);
        await subscription.unsubscribe();
      }
    } catch {
      /* Logout must still clear the local session. */
    }
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      navigate("/login", { replace: true });
    }
  }
  async function clearLocalCache() {
    if (
      !(await modal.confirm("清理本地缓存不会退出当前账号，确定继续吗？", {
        title: "清理本地缓存",
        confirmLabel: "清理",
      }))
    )
      return;
    sessionStorage.removeItem("monstera_pwa_pending_chat_job");
    if ("caches" in window)
      await Promise.all((await caches.keys()).map((key) => caches.delete(key)));
    setSuccess("本地缓存已清理");
  }
  async function openNotifications() {
    setView("notifications");
    begin("notifications-load");
    try {
      const config = await api.getPushConfig();
      setPushConfig(config);
      const subscription =
        push.supported() && push.permission() !== "denied"
          ? await push.current().catch(() => null)
          : null;
      setPushSubscription(subscription);
    } catch (reason) {
      setError(message(reason, "通知设置加载失败"));
    } finally {
      finish();
    }
  }
  function showNotificationGuide() {
    setInfo({
      title: "开启系统通知",
      content:
        "iPhone/iPad：先将应用添加到主屏幕，再打开“设置 > 通知 > 昭游智伴侣”并允许通知。Android：长按应用图标，进入“应用信息 > 通知”后允许通知。完成后返回这里再次点击开启通知。",
    });
  }
  async function enableNotifications() {
    if (!pushConfig?.enabled || !pushConfig.publicKey) {
      setError("系统通知尚未配置");
      return;
    }
    if (push.permission() === "denied") {
      showNotificationGuide();
      return;
    }
    begin("notifications-enable");
    try {
      const subscription = await push.subscribe(pushConfig.publicKey);
      await api.savePushSubscription(push.input(subscription));
      setPushSubscription(subscription);
      setSuccess("系统通知已开启");
    } catch (reason) {
      const text = message(reason, "系统通知开启失败");
      setError(text);
      if (text.includes("通知权限被系统拒绝")) showNotificationGuide();
    } finally {
      finish();
    }
  }
  async function disableNotifications() {
    if (!pushSubscription) return;
    begin("notifications-disable");
    try {
      await api.deletePushSubscription(pushSubscription.endpoint);
      await pushSubscription.unsubscribe();
      setPushSubscription(null);
      setSuccess("系统通知已关闭");
    } catch (reason) {
      setError(message(reason, "系统通知关闭失败"));
    } finally {
      finish();
    }
  }
  async function sendTestNotification() {
    if (!pushSubscription) return;
    begin("notifications-test");
    try {
      await api.sendTestPush(pushSubscription.endpoint);
      setSuccess("测试通知已发送");
    } catch (reason) {
      setError(message(reason, "测试通知发送失败"));
    } finally {
      finish();
    }
  }

  const title = {
    home: "我的",
    edit: "编辑资料",
    security: "账号安全",
    notifications: "系统通知",
    display: "显示设置",
  }[view];
  return (
    <main className="profile-shell">
      <header className="profile-header">
        {view === "home" ? (
          <Link to="/chat">返回聊天</Link>
        ) : (
          <button
            className="profile-back-button"
            onClick={() => {
              setView("home");
              setError("");
              setSuccess("");
            }}
          >
            返回我的
          </button>
        )}
        <h1>{title}</h1>
        <span />
      </header>
      <div className="profile-content">
        {loading && <LoadingIndicator label="资料加载中…" />}
        {error && (
          <p className="chat-error" role="alert">
            {error}
          </p>
        )}
        {success && (
          <p className="profile-success" role="status">
            {success}
          </p>
        )}
        {!loading && !profile && !error && <p>暂无个人资料</p>}
        {view === "home" && profile && (
          <>
            <button
              className="profile-summary-card"
              aria-label="编辑个人资料"
              onClick={() => setView("edit")}
            >
              <span className="profile-summary-main">
                <span className="profile-summary-avatar">
                  {avatarUrl ? (
                    <img src={resolveApiUrl(avatarUrl)} alt="当前头像" />
                  ) : (
                    <span>{(name || "用").slice(0, 1)}</span>
                  )}
                </span>
                <span className="profile-summary-copy">
                  <strong>{profile.name}</strong>
                  <small>编辑资料</small>
                </span>
              </span>
              <span className="profile-action-arrow">›</span>
            </button>
            <ProfileMenuCard>
              <ProfileMenuButton
                icon="service"
                title="客服中心"
                onClick={() =>
                  setInfo({
                    title: "客服中心",
                    content: "如需帮助，请联系昭游智伴侣运营人员。",
                  })
                }
              />
            </ProfileMenuCard>
            <ProfileMenuCard>
              <ProfileMenuButton
                icon="shield"
                title="账号安全"
                onClick={() => {
                  setSecurityPanel(null);
                  setView("security");
                }}
              />
              <ProfileMenuButton
                icon="setting"
                title="系统通知"
                onClick={() => void openNotifications()}
              />
              <ProfileMenuButton
                icon="setting"
                title="显示设置"
                onClick={() => setView("display")}
              />
              <ProfileMenuButton
                icon="shield"
                title="隐私与协议"
                onClick={() =>
                  setInfo({
                    title: "隐私与协议",
                    content:
                      "使用昭游智伴侣即表示您同意用户服务协议与隐私政策。",
                  })
                }
              />
            </ProfileMenuCard>
            <ProfileMenuCard>
              <ProfileMenuButton
                icon="refresh"
                title="清理本地缓存"
                onClick={() => void clearLocalCache()}
              />
              <ProfileMenuButton
                icon="info"
                title="关于昭游智伴侣"
                onClick={() =>
                  setInfo({
                    title: "关于昭游智伴侣",
                    content: "昭游智伴侣 · 昭通避暑文旅智能顾问",
                  })
                }
              />
            </ProfileMenuCard>
            <ProfileMenuCard>
              <ProfileMenuButton
                icon="logout"
                title="退出登录"
                danger
                onClick={() => void handleLogout()}
              />
            </ProfileMenuCard>
          </>
        )}
        {view === "edit" && profile && (
          <section className="profile-card">
            <div className="profile-avatar">
              {avatarUrl ? (
                <img src={resolveApiUrl(avatarUrl)} alt="当前头像" />
              ) : (
                <span>{(name || "用").slice(0, 1)}</span>
              )}
              <div>
                <strong>{profile.name}</strong>
                <small>@{profile.username}</small>
              </div>
            </div>
            <form className="profile-form" onSubmit={saveProfile}>
              <label>
                昵称
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label>
                头像
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    setAvatar(event.target.files?.[0] || null)
                  }
                />
              </label>
              <button disabled={Boolean(busy)}>
                {busy === "profile" ? (
                  <LoadingIndicator compact label="保存中…" />
                ) : (
                  "保存资料"
                )}
              </button>
            </form>
          </section>
        )}
        {view === "security" && (
          <div className="security-sections">
            {securityLoading && (
              <section className="profile-card security-card">
                <LoadingIndicator label="账号安全信息加载中…" />
              </section>
            )}
            {securityError && <p className="chat-error">{securityError}</p>}
            {security && (
              <>
                <section className="profile-card security-card">
                  <div className="security-section-header">
                    <div>
                      <h2>账号安全</h2>
                      <p>管理你的验证方式和登录密码</p>
                    </div>
                    <span className="security-badge">安全</span>
                  </div>
                  <dl className="security-status-list">
                    <div>
                      <dt>邮箱</dt>
                      <dd
                        className={security.emailVerified ? "is-verified" : ""}
                      >
                        {security.emailVerified ? security.email : "未验证"}
                      </dd>
                    </div>
                    <div>
                      <dt>微信</dt>
                      <dd className={security.wechatBound ? "is-verified" : ""}>
                        {security.wechatBound ? "已绑定" : "未绑定"}
                      </dd>
                    </div>
                  </dl>
                </section>
                {securityPanel !== "email" ? (
                  <button
                    className="security-action-card"
                    type="button"
                    onClick={() => setSecurityPanel("email")}
                  >
                    <span>
                      <strong>
                        {security.emailVerified ? "更换邮箱" : "绑定邮箱"}
                      </strong>
                      <small>用于找回密码和接收安全提醒</small>
                    </span>
                    <b>›</b>
                  </button>
                ) : (
                  <section className="profile-card security-card">
                    <div className="security-section-header">
                      <div>
                        <h2>
                          {security.emailVerified ? "更换邮箱" : "绑定邮箱"}
                        </h2>
                        <p>用于找回密码和接收安全提醒</p>
                      </div>
                    </div>
                    <form
                      className="profile-form security-form"
                      onSubmit={bindEmail}
                    >
                      <label>
                        邮箱
                        <input
                          type="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          placeholder="请输入常用邮箱"
                          autoComplete="email"
                        />
                      </label>
                      <div className="verification-row">
                        <label>
                          验证码
                          <input
                            value={emailCode}
                            onChange={(event) =>
                              setEmailCode(event.target.value)
                            }
                            placeholder="6位验证码"
                            inputMode="numeric"
                          />
                        </label>
                        <button
                          type="button"
                          className="secondary-action"
                          disabled={Boolean(busy)}
                          onClick={() => void sendEmailCode()}
                        >
                          {busy === "email-code" ? (
                            <LoadingIndicator compact label="发送中…" />
                          ) : (
                            "发送验证码"
                          )}
                        </button>
                      </div>
                      <button
                        className="security-primary-action"
                        disabled={Boolean(busy)}
                      >
                        {busy === "email" ? (
                          <LoadingIndicator compact label="绑定中…" />
                        ) : security.emailVerified ? (
                          "确认更换邮箱"
                        ) : (
                          "绑定邮箱"
                        )}
                      </button>
                    </form>
                  </section>
                )}
                {securityPanel !== "password" ? (
                  <button
                    className="security-action-card"
                    type="button"
                    onClick={() => setSecurityPanel("password")}
                  >
                    <span>
                      <strong>修改密码</strong>
                      <small>建议使用 8 位以上且不易猜测的密码</small>
                    </span>
                    <b>›</b>
                  </button>
                ) : (
                  <section className="profile-card security-card">
                    <div className="security-section-header">
                      <div>
                        <h2>修改密码</h2>
                        <p>建议使用 8 位以上且不易猜测的密码</p>
                      </div>
                    </div>
                    <form
                      className="profile-form security-form"
                      onSubmit={changePassword}
                    >
                      <label>
                        当前密码
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(event) =>
                            setCurrentPassword(event.target.value)
                          }
                          autoComplete="current-password"
                          placeholder="请输入当前密码"
                        />
                      </label>
                      <label>
                        新密码
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(event) =>
                            setNewPassword(event.target.value)
                          }
                          autoComplete="new-password"
                          placeholder="请输入新密码"
                        />
                      </label>
                      <label>
                        确认新密码
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(event) =>
                            setConfirmPassword(event.target.value)
                          }
                          autoComplete="new-password"
                          placeholder="再次输入新密码"
                        />
                      </label>
                      <button
                        className="security-primary-action"
                        disabled={Boolean(busy)}
                      >
                        {busy === "password" ? (
                          <LoadingIndicator compact label="修改中…" />
                        ) : (
                          "修改密码"
                        )}
                      </button>
                    </form>
                  </section>
                )}
              </>
            )}
          </div>
        )}
        {view === "display" && (
          <section className="profile-card display-settings">
            <div className="security-section-header">
              <div>
                <h2>字体大小</h2>
                <p>选择适合你的阅读字号，设置会自动保存</p>
              </div>
            </div>
            <div
              className="font-size-options"
              role="radiogroup"
              aria-label="字体大小"
            >
              {(["small", "medium", "large"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={fontSize === value}
                  className={fontSize === value ? "active" : ""}
                  onClick={() => selectFontSize(value)}
                >
                  <span className={`font-size-sample ${value}`}>Aa</span>
                  <strong>
                    {{ small: "小", medium: "中", large: "大" }[value]}
                  </strong>
                  <span className="font-size-check" aria-hidden="true">
                    ✓
                  </span>
                </button>
              ))}
            </div>
            <div className="font-size-preview">
              <small>预览</small>
              <p>昭通夏季清凉舒适，适合避暑旅行。</p>
            </div>
          </section>
        )}
        {view === "notifications" && (
          <section className="profile-card notification-settings">
            <h2>系统通知</h2>
            {busy === "notifications-load" ? (
              <LoadingIndicator label="通知设置加载中…" />
            ) : (
              <>
                <p>
                  {notificationStatus(
                    push.supported(),
                    push.permission(),
                    pushConfig?.enabled,
                    Boolean(pushSubscription),
                  )}
                </p>
                <button
                  disabled={
                    Boolean(busy) || !push.supported() || !pushConfig?.enabled
                  }
                  onClick={() =>
                    void (pushSubscription
                      ? disableNotifications()
                      : enableNotifications())
                  }
                >
                  {busy.startsWith("notifications-") ? (
                    <LoadingIndicator compact label="处理中…" />
                  ) : pushSubscription ? (
                    "关闭通知"
                  ) : (
                    "开启通知"
                  )}
                </button>
                {pushSubscription && (
                  <button
                    className="secondary"
                    disabled={Boolean(busy)}
                    onClick={() => void sendTestNotification()}
                  >
                    {busy === "notifications-test" ? (
                      <LoadingIndicator compact label="发送中…" />
                    ) : (
                      "发送测试通知"
                    )}
                  </button>
                )}
              </>
            )}
          </section>
        )}
      </div>
      {info && (
        <div
          className="profile-info-mask"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setInfo(null);
          }}
        >
          <section
            className="profile-info-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={info.title}
          >
            <h2>{info.title}</h2>
            <p>{info.content}</p>
            <button autoFocus onClick={() => setInfo(null)}>
              知道了
            </button>
          </section>
        </div>
      )}
      {modal.element}
    </main>
  );
}

function ProfileMenuCard({ children }: { children: ReactNode }) {
  return <section className="profile-menu-card">{children}</section>;
}

function ProfileMenuButton({
  icon,
  title,
  danger = false,
  onClick,
}: {
  icon: "service" | "shield" | "setting" | "refresh" | "info" | "logout";
  title: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={danger ? "profile-action-row danger" : "profile-action-row"}
      aria-label={title}
      onClick={onClick}
    >
      <span className="profile-action-main">
        <span className="profile-menu-icon">
          <img src={publicAsset(`icons/profile-${icon}.svg`)} alt="" />
        </span>
        <strong>{title}</strong>
      </span>
      <span className="profile-action-arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

function message(reason: unknown, fallback: string) {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}
function notificationStatus(
  supported: boolean,
  permission: NotificationPermission | "unsupported",
  configured = false,
  subscribed = false,
) {
  if (!supported) return "当前浏览器不支持系统通知。";
  if (!configured) return "服务端尚未配置系统通知。";
  if (permission === "denied")
    return "通知权限已被系统拒绝，请在浏览器设置中重新允许。";
  return subscribed
    ? "系统通知已开启，可接收行程和服务提醒。"
    : "开启后可在 PWA 关闭时接收重要提醒。";
}
